import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, FileUp, FileCheck2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { getGdpr } from "@/i18n/gdpr";
import { Link } from "react-router-dom";

const empty = {
  prenom: "", nom: "", email: "", telephone: "", parti: "",
  profession: "", region: "", vision: "", programme: "",
};

const fieldCls =
  "bg-black/50 border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-theatre-gold focus-visible:border-theatre-gold rounded-none h-12";
const areaCls =
  "bg-black/50 border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-theatre-gold focus-visible:border-theatre-gold rounded-none";

export const PreinscriptionForm = () => {
  const { t, lang } = useI18n();
  const g = getGdpr(lang);
  const [form, setForm] = useState(empty);
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error(t("form.errPdf")); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error(t("form.errSize")); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error(t("form.errAccept")); return; }
    setLoading(true);
    try {
      let dossier = { dossier_file_id: null, dossier_filename: null };
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/dossier/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        dossier = { dossier_file_id: data.file_id, dossier_filename: data.filename };
      }
      await api.post("/candidates", { ...form, ...dossier, accepte_conditions: accept });
      setDone(true);
      toast.success(t("form.successToast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("form.errSend"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section id="preinscription" data-testid="preinscription-section" className="relative py-28 md:py-36 px-6 lg:px-10">
        <div className="max-w-xl mx-auto text-center bg-black/60 backdrop-blur-xl border border-theatre-gold/30 p-12" data-testid="preinscription-success">
          <CheckCircle2 className="mx-auto text-theatre-gold" size={56} strokeWidth={1.2} />
          <h2 className="mt-6 font-serif text-3xl text-white">{t("form.successTitle")}</h2>
          <p className="mt-4 text-neutral-400 leading-relaxed">{t("form.successText", { name: form.prenom })}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="preinscription" data-testid="preinscription-section" className="relative py-28 md:py-36 px-6 lg:px-10">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-theatre-gold mb-5">{t("form.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white">{t("form.title")}</h2>
          <p className="mt-5 text-neutral-400">{t("form.subtitle")}</p>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          data-testid="preinscription-form"
          className="bg-black/50 backdrop-blur-xl border border-white/10 p-8 md:p-12 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.prenom")} *</Label>
              <Input data-testid="input-prenom" required value={form.prenom} onChange={set("prenom")} className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.nom")} *</Label>
              <Input data-testid="input-nom" required value={form.nom} onChange={set("nom")} className={fieldCls} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.email")} *</Label>
              <Input data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.telephone")} *</Label>
              <Input data-testid="input-telephone" required value={form.telephone} onChange={set("telephone")} className={fieldCls} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.parti")}</Label>
              <Input data-testid="input-parti" value={form.parti} onChange={set("parti")} placeholder={t("form.partiPh")} className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.profession")} *</Label>
              <Input data-testid="input-profession" required value={form.profession} onChange={set("profession")} className={fieldCls} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.region")} *</Label>
            <Input data-testid="input-region" required value={form.region} onChange={set("region")} className={fieldCls} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.vision")} *</Label>
            <Textarea data-testid="input-vision" required value={form.vision} onChange={set("vision")} rows={4} placeholder={t("form.visionPh")} className={areaCls} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.programme")}</Label>
            <Textarea data-testid="input-programme" value={form.programme} onChange={set("programme")} rows={3} className={areaCls} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">{t("form.dossier")}</Label>
            <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} className="hidden" data-testid="input-dossier" />
            {!file ? (
              <button
                type="button"
                data-testid="dossier-choose-btn"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-3 border border-dashed border-white/20 hover:border-theatre-gold/60 text-neutral-400 hover:text-theatre-gold py-5 transition-colors duration-200"
              >
                <FileUp size={18} /> {t("form.dossierChoose")}
              </button>
            ) : (
              <div className="flex items-center justify-between border border-theatre-gold/40 bg-black/40 px-4 py-3" data-testid="dossier-selected">
                <span className="flex items-center gap-2 text-sm text-theatre-gold truncate">
                  <FileCheck2 size={16} /> {file.name}
                </span>
                <button type="button" data-testid="dossier-remove-btn" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-neutral-400 hover:text-theatre-red">
                  <X size={16} />
                </button>
              </div>
            )}
            <p className="text-[11px] text-neutral-600">{t("form.dossierHint")}</p>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox id="accept" data-testid="input-accept" checked={accept} onCheckedChange={setAccept} className="mt-1 border-theatre-gold/60 data-[state=checked]:bg-theatre-red data-[state=checked]:border-theatre-red" />
            <label htmlFor="accept" className="text-sm text-neutral-400 leading-relaxed cursor-pointer">{t("form.accept")}</label>
          </div>

          <p className="text-xs text-neutral-500 leading-relaxed border-l-2 border-theatre-gold/40 pl-3" data-testid="form-rgpd-note">
            {g.formRgpd}{" "}
            <Link to="/confidentialite" className="text-theatre-gold underline hover:text-theatre-gold/80">{g.seePolicy}</Link>
          </p>

          <button
            type="submit"
            data-testid="preinscription-submit"
            disabled={loading}
            className="w-full bg-theatre-red hover:bg-theatre-redhover disabled:opacity-60 text-white py-4 text-xs uppercase tracking-[0.25em] transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            {loading ? t("form.submitting") : t("form.submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
